import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@components/common/Modal/Modal';
import Button from '@components/Button/Button';
import { resolveBackendFileUrl } from '@utils/backendUrl';
import evaluationsService, {
    computeWeightedTotal,
    type EvaluationStatus,
} from '@/services/evaluations/evaluationsService';
import JudgeSelect from './JudgeSelect';

interface Props {
    enrollmentId: string;
    onClose: () => void;
    onSaved: () => void;
    onPrev?: () => void;
    onNext?: () => void;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
    /** 0-based position + count of the list being stepped through (shows an "N of M" bar). */
    index?: number;
    total?: number;
    /** Pre-selects a judge (e.g. the one picked for the previous student in the same session). */
    defaultJudgeId?: string;
    /** Fires whenever the judge selection changes, so a parent can remember it across students. */
    onJudgeChange?: (judgeId: string) => void;
}

type ScoreState = Record<string, { score: number; key_evidence: string; comments: string }>;

const EvaluationForm: React.FC<Props> = ({
    enrollmentId,
    onClose,
    onSaved,
    onPrev,
    onNext,
    isPrevDisabled,
    isNextDisabled,
    index,
    total,
    defaultJudgeId,
    onJudgeChange,
}) => {
    const { t } = useTranslation('evaluation');
    const queryClient = useQueryClient();

    const { data: detail, isLoading, isError } = useQuery({
        queryKey: ['evaluation', enrollmentId],
        queryFn: () => evaluationsService.getOne(enrollmentId),
    });

    const [judgeId, setJudgeId] = useState(defaultJudgeId ?? '');
    const [scores, setScores] = useState<ScoreState>({});
    const [serverErrors, setServerErrors] = useState<string[]>([]);
    const [expandedGuidance, setExpandedGuidance] = useState<Record<string, boolean>>({});
    const dirtyRef = useRef(false);

    const rubric = detail?.rubric;
    const myScorecard = useMemo(
        () => detail?.judges.find((j) => j.judge.id === judgeId) ?? null,
        [detail, judgeId],
    );

    // Seed local score state whenever the rubric loads or the selected judge changes.
    useEffect(() => {
        if (!rubric) return;
        const next: ScoreState = {};
        for (const s of rubric.sections) {
            const existing = myScorecard?.section_scores.find((x) => x.section_key === s.key);
            next[s.key] = {
                score: existing?.score ?? 0,
                key_evidence: existing?.key_evidence ?? '',
                comments: existing?.comments ?? '',
            };
        }
        setScores(next);
        setServerErrors([]);
        dirtyRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rubric, judgeId, myScorecard?.updated_at]);

    // Warn before stepping away from unsaved edits (prev/next student, or switching judge).
    const guarded = (fn?: () => void) =>
        fn
            ? () => {
                  if (dirtyRef.current && !window.confirm(t('form.unsavedWarn'))) return;
                  fn();
              }
            : undefined;

    const handleJudgeChange = (id: string) => {
        const apply = () => {
            setJudgeId(id);
            onJudgeChange?.(id);
        };
        if (dirtyRef.current && !window.confirm(t('form.unsavedWarn'))) return;
        apply();
    };

    const totals = useMemo(() => {
        if (!rubric) return { totalRaw: 0, totalWeighted: 0 };
        return computeWeightedTotal(
            rubric.sections.map((s) => ({ key: s.key, weight: s.weight })),
            scores,
            rubric.scale_max,
        );
    }, [rubric, scores]);

    const allScored = useMemo(
        () => !!rubric && rubric.sections.filter((s) => s.key !== 'presentation').every((s) => (scores[s.key]?.score ?? 0) >= rubric.scale_min),
        [rubric, scores],
    );

    const mutation = useMutation({
        mutationFn: (status: EvaluationStatus) =>
            evaluationsService.upsert(enrollmentId, {
                judge_id: judgeId,
                status,
                section_scores: (rubric?.sections ?? []).map((s) => ({
                    section_key: s.key,
                    score: scores[s.key]?.score ?? 0,
                    key_evidence: scores[s.key]?.key_evidence?.trim() || undefined,
                    comments: scores[s.key]?.comments?.trim() || undefined,
                })),
            }),
        onSuccess: (_data, status) => {
            dirtyRef.current = false;
            toast.success(status === 'SUBMITTED' ? t('form.submitted') : t('form.saved'));
            queryClient.invalidateQueries({ queryKey: ['evaluation', enrollmentId] });
            onSaved();
        },
        onError: (err: any) => {
            const payload = err?.response?.data;
            const list: string[] = Array.isArray(payload?.errors)
                ? payload.errors
                : Array.isArray(payload?.message)
                    ? payload.message
                    : [payload?.message || t('form.saveError')];
            setServerErrors(list);
            toast.error(t('form.saveError'));
        },
    });

    const setScore = (key: string, patch: Partial<ScoreState[string]>) => {
        dirtyRef.current = true;
        setScores((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    };

    const title = detail ? t('form.titleFor', { name: detail.enrollment.student.name }) : t('title');
    const goPrev = guarded(onPrev);
    const goNext = guarded(onNext);
    const otherJudges = useMemo(
        () => detail?.judges.filter((j) => j.judge.id !== judgeId) ?? [],
        [detail, judgeId],
    );

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={title}
            maxWidth="max-w-[980px]"
            onPrev={goPrev}
            onNext={goNext}
            isPrevDisabled={isPrevDisabled}
            isNextDisabled={isNextDisabled}
        >
            <div className="flex flex-col max-h-[calc(90vh-52px)]">
                {isLoading && (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary-color)]" />
                    </div>
                )}

                {isError && (
                    <div className="p-6 text-sm text-red-600">{t('grid.loadError')}</div>
                )}

                {detail && rubric && (
                    <>
                        {/* ── Student header ── */}
                        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/70 shrink-0">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                                <span className="font-semibold text-gray-800">{detail.enrollment.student.name}</span>
                                <span>·</span>
                                <span>{detail.enrollment.enrollment_number}</span>
                                <span>·</span>
                                <span>{detail.enrollment.cohort.title}</span>
                                {detail.enrollment.mentor && (
                                    <>
                                        <span>·</span>
                                        <span>Mentor: {detail.enrollment.mentor.name}</span>
                                    </>
                                )}
                                {detail.enrollment.team_number != null && (
                                    <>
                                        <span>·</span>
                                        <span>Team {detail.enrollment.team_number}</span>
                                    </>
                                )}
                            </div>
                            <div className="mt-1.5 text-xs text-gray-400">
                                {t('form.rubricFrom', { name: rubric.name, version: rubric.template_version })}
                            </div>
                        </div>

                        {/* ── Sections ── */}
                        <div className="overflow-y-auto px-5 py-4 space-y-4">
                            {/* Judge picker — everything below is scoped to whichever judge is selected */}
                            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
                                <div className="border border-gray-200 rounded-xl p-3">
                                    <JudgeSelect value={judgeId} onChange={handleJudgeChange} required />
                                    {judgeId && (
                                        <p className="mt-2 text-[11px] text-gray-500">
                                            {myScorecard
                                                ? t('form.thisIsExisting', {
                                                      name: detail.judges.find((j) => j.judge.id === judgeId)?.judge.name,
                                                  })
                                                : t('form.thisIsNew', {
                                                      name:
                                                          detail.judges.find((j) => j.judge.id === judgeId)?.judge.name ??
                                                          '',
                                                  })}
                                        </p>
                                    )}
                                    {!judgeId && <p className="mt-2 text-[11px] text-amber-600">{t('form.pickJudgeFirst')}</p>}
                                </div>

                                <div className="border border-gray-200 rounded-xl p-3 flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="text-[11px] font-semibold uppercase text-gray-400">
                                            {t('form.otherJudges')}
                                        </div>
                                        {otherJudges.length === 0 ? (
                                            <p className="text-xs text-gray-400 mt-1">{t('form.noOtherJudges')}</p>
                                        ) : (
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                {otherJudges.map((j) => (
                                                    <span
                                                        key={j.judge.id}
                                                        className={[
                                                            'text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap',
                                                            j.status === 'SUBMITTED'
                                                                ? 'bg-green-50 text-green-700'
                                                                : 'bg-amber-50 text-amber-700',
                                                        ].join(' ')}
                                                        title={j.status === 'SUBMITTED' ? String(j.total_weighted) : t('status.DRAFT')}
                                                    >
                                                        {j.judge.name}
                                                        {j.status === 'SUBMITTED' ? ` · ${j.total_weighted}` : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[11px] font-semibold uppercase text-gray-400">
                                            {t('form.finalScore')}
                                        </div>
                                        <div className="text-xl font-extrabold text-gray-900">
                                            {detail.final.total_weighted}
                                            <span className="text-xs font-medium text-gray-400"> / 100</span>
                                        </div>
                                        <div className="text-[10px] text-gray-400">
                                            {t(detail.final.judges_counted === 1 ? 'form.judgesCounted' : 'form.judgesCounted_plural', {
                                                n: detail.final.judges_counted,
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Student's project — the reference for scoring (from their profile) */}
                            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-bold text-gray-900">
                                        {t('form.project')}
                                        <span className="ml-2 text-[11px] font-normal text-gray-400">
                                            {t('form.projectFrom')}
                                        </span>
                                    </div>
                                    {detail.project?.demo_presentation_path && (
                                        <a
                                            href={resolveBackendFileUrl(detail.project.demo_presentation_path)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs font-semibold text-[var(--primary-color)] hover:underline whitespace-nowrap"
                                        >
                                            {detail.project.demo_presentation_name || t('form.viewDeck')}
                                        </a>
                                    )}
                                </div>

                                {detail.project?.project_title || detail.project?.project_description ? (
                                    <div className="mt-2 space-y-2">
                                        {detail.project?.project_title && (
                                            <div className="text-sm font-semibold text-gray-800">
                                                {detail.project.project_title}
                                            </div>
                                        )}
                                        {detail.project?.project_description && (
                                            <div>
                                                <div className="text-[11px] font-semibold uppercase text-gray-400 mb-0.5">
                                                    {t('form.projectDescription')}
                                                </div>
                                                <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed max-h-40 overflow-y-auto bg-white border border-gray-100 rounded-lg p-2.5">
                                                    {detail.project.project_description}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="mt-2 text-xs text-gray-400 italic">{t('form.noProject')}</p>
                                )}
                            </div>

                            {serverErrors.length > 0 && (
                                <ul className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-1 list-disc list-inside">
                                    {serverErrors.map((e, i) => (
                                        <li key={i}>{e}</li>
                                    ))}
                                </ul>
                            )}

                            <fieldset disabled={!judgeId} className={!judgeId ? 'opacity-50' : undefined}>
                                <div className="space-y-4">
                                    {rubric.sections.map((section) => {
                                        const cur = scores[section.key] ?? { score: 0, key_evidence: '', comments: '' };
                                        const weighted = (cur.score / (rubric.scale_max || 1)) * (section.weight * 100);
                                        return (
                                            <div key={section.key} className="border border-gray-200 rounded-xl p-4">
                                                <div className="flex flex-wrap items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <div className="text-sm font-bold text-gray-900">{section.title}</div>
                                                            {section.key === 'presentation' && (
                                                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                                                                    Optional
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{section.criteria_name}</div>
                                                    </div>
                                                    <span className="text-[11px] font-semibold text-gray-400 whitespace-nowrap">
                                                        {t('form.sectionWeight', { weight: Math.round(section.weight * 100) })}
                                                    </span>
                                                </div>

                                                <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                                                    <span className="font-semibold text-gray-700">{t('form.judgesLookFor')}: </span>
                                                    {section.judges_look_for}
                                                </p>

                                                {section.guidance_items.length > 0 && (
                                                    <div className="mt-2">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setExpandedGuidance((p) => ({ ...p, [section.key]: !p[section.key] }))
                                                            }
                                                            className="text-[11px] font-semibold text-[var(--primary-color)] hover:underline"
                                                        >
                                                            {expandedGuidance[section.key] ? '− ' : '+ '}
                                                            {t('form.scoringGuidance')}
                                                        </button>
                                                        {expandedGuidance[section.key] && (
                                                            <ul className="mt-1.5 space-y-1.5 text-[11px] text-gray-500">
                                                                {section.guidance_items.map((g) => (
                                                                    <li key={g.label}>
                                                                        <span className="font-semibold text-gray-700">{g.label}:</span>{' '}
                                                                        {g.question}
                                                                        <div className="text-gray-400">{g.scoring_guidance}</div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}

                                                {/* score buttons */}
                                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-semibold text-gray-600 mr-1">{t('form.sectionScore')}:</span>
                                                    {Array.from(
                                                        { length: rubric.scale_max - rubric.scale_min + 1 },
                                                        (_, i) => rubric.scale_min + i,
                                                    ).map((n) => (
                                                        <button
                                                            key={n}
                                                            type="button"
                                                            onClick={() => setScore(section.key, { score: n })}
                                                            title={rubric.scale_labels[String(n)] ?? ''}
                                                            className={[
                                                                'w-8 h-8 rounded-lg text-xs font-bold border transition-colors',
                                                                cur.score === n
                                                                    ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)]'
                                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
                                                            ].join(' ')}
                                                        >
                                                            {n}
                                                        </button>
                                                    ))}
                                                    <div className="flex items-center gap-1.5 ml-1 border-l border-gray-200 pl-2.5">
                                                        <span className="text-[11px] text-gray-500 font-medium">Or:</span>
                                                        <input
                                                            type="number"
                                                            min={rubric.scale_min}
                                                            max={rubric.scale_max}
                                                            step="0.1"
                                                            value={cur.score > 0 ? cur.score : ''}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value);
                                                                setScore(section.key, { score: isNaN(val) ? 0 : val });
                                                            }}
                                                            placeholder="Custom"
                                                            className="w-16 h-8 rounded-lg border border-gray-200 px-2 text-xs font-bold text-center outline-none focus:border-[var(--primary-color)]"
                                                        />
                                                    </div>
                                                    <span className="text-[11px] text-gray-400 ml-1">
                                                        {cur.score > 0
                                                            ? `${rubric.scale_labels[String(cur.score)] ? `${rubric.scale_labels[String(cur.score)]} · ` : ''}${t('form.weightedContribution', {
                                                                  value: weighted.toFixed(1),
                                                                  max: Math.round(section.weight * 100),
                                                              })}`
                                                            : t('form.unscored')}
                                                    </span>
                                                </div>

                                                <div className="mt-3 grid gap-2 md:grid-cols-2">
                                                    <label className="block">
                                                        <span className="text-[11px] font-semibold text-gray-600">{t('form.keyEvidence')}</span>
                                                        <textarea
                                                            rows={2}
                                                            value={cur.key_evidence}
                                                            onChange={(e) => setScore(section.key, { key_evidence: e.target.value })}
                                                            placeholder={t('form.keyEvidencePlaceholder')}
                                                            className="mt-1 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-[var(--primary-color)] resize-none"
                                                        />
                                                    </label>
                                                    <label className="block">
                                                        <span className="text-[11px] font-semibold text-gray-600">{t('form.comments')}</span>
                                                        <textarea
                                                            rows={2}
                                                            value={cur.comments}
                                                            onChange={(e) => setScore(section.key, { comments: e.target.value })}
                                                            placeholder={t('form.commentsPlaceholder')}
                                                            className="mt-1 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs outline-none focus:border-[var(--primary-color)] resize-none"
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </fieldset>
                        </div>

                        {/* ── Footer ── */}
                        <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-2 shrink-0">
                            {typeof total === 'number' && total > 1 && (
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={goPrev}
                                        disabled={isPrevDisabled}
                                        title={t('form.prevStudent')}
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span className="text-xs font-semibold text-gray-500 min-w-[64px] text-center tabular-nums">
                                        {(index ?? 0) + 1} <span className="font-normal text-gray-400">/</span> {total}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={goNext}
                                        disabled={isNextDisabled}
                                        title={t('form.nextStudent')}
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}

                            <div className="text-sm">
                                <span className="text-gray-500">{t('form.grandTotal')}: </span>
                                <span className="text-lg font-extrabold text-gray-900">{totals.totalWeighted}</span>
                                <span className="text-gray-400"> {t('form.outOf100')}</span>
                                {rubric.pass_mark != null && (
                                    <span
                                        className={[
                                            'ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full',
                                            totals.totalWeighted >= rubric.pass_mark
                                                ? 'bg-green-50 text-green-700'
                                                : 'bg-amber-50 text-amber-700',
                                        ].join(' ')}
                                    >
                                        {t('form.passMark', { value: rubric.pass_mark })}
                                    </span>
                                )}
                                {myScorecard?.updated_at && (
                                    <span className="ml-3 text-[11px] text-gray-400">
                                        {t('form.lastSaved', {
                                            when: new Date(myScorecard.updated_at).toLocaleString(),
                                            who: myScorecard.evaluator_name ?? '—',
                                        })}
                                    </span>
                                )}
                            </div>
                            <div className="ml-auto flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => mutation.mutate('DRAFT')}
                                    isLoading={mutation.isPending && mutation.variables === 'DRAFT'}
                                    disabled={mutation.isPending || !judgeId}
                                    title={!judgeId ? t('judge.required') : ''}
                                >
                                    {t('form.saveDraft')}
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => mutation.mutate('SUBMITTED')}
                                    isLoading={mutation.isPending && mutation.variables === 'SUBMITTED'}
                                    disabled={mutation.isPending || !judgeId || !allScored}
                                    title={!judgeId ? t('judge.required') : !allScored ? t('form.submitNeedsAll') : ''}
                                >
                                    {myScorecard?.status === 'SUBMITTED' ? t('form.resubmit') : t('form.submit')}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default EvaluationForm;
